import React from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  ShortRight,
  ExternalLink,
  CircleCheck,
  HelpCircle,
  Refresh,
} from "@tiny/icons";
import { isFreeDomain } from "@/routes/AccountSettings/SenderDetails/utils";
import useDnsAuthentication from "./DNSAuthentication.hooks";

interface AuthStatusIndicatorProps {
  isFree: boolean;
  isSigned: boolean;
  protocol: string;
  domain?: string;
}

const AuthStatusIndicator = ({
  isFree,
  isSigned,
  protocol,
  domain,
}: AuthStatusIndicatorProps) => {
  let icon = <HelpCircle color="primary" />;
  let text = `${protocol} needs to be configured`;
  if (isFree) {
    text = `${protocol} is N/A`;
  } else if (isSigned) {
    icon = <CircleCheck color="primary" />;
    text = `${protocol} is verified for ${domain ?? ""}`;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {icon}
      <Typography>{text}</Typography>
    </Box>
  );
};

const DNSAuthentication = () => {
  const [selectedDomain, setSelectedDomain] = React.useState<string>();
  const [recheckFlag, setRecheckFlag] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const isSelectedDomainFree = React.useMemo(
    () => isFreeDomain(selectedDomain || ""),
    [selectedDomain]
  );
  const {
    domains,
    fetchAuthenticationStatus,
    authStatus,
    authError,
    navigateToDNSConfig,
  } = useDnsAuthentication();

  const checkAuthenticationStatus = async (domain: string) => {
    setLoading(true);
    await fetchAuthenticationStatus(domain);
    setLoading(false);
  };

  const handleChangeSelectedDomain = async (domain: string) => {
    setSelectedDomain(domain);
    await checkAuthenticationStatus(domain);
  };

  return (
    <Box
      sx={(theme) => ({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        mt: 4,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        borderWidth: 1,
        outlineColor: theme.palette.divider,
      })}
    >
      <Box
        sx={() => ({
          display: "flex",
          flexDirection: "column",
          py: 5,
          px: 6,
          gap: 1,
        })}
      >
        <Typography variant="h3">DNS Settings</Typography>
        <Typography
          variant="p2"
          sx={(theme) => ({
            color: theme.palette.grey[80],
          })}
        >
          Boost your email deliverability and open rates by setting up domain
          authentication. Without it, your emails are more likely to end up in
          the spam folder, reducing their effectiveness. With proper DNS
          settings, your emails will reach the inbox, boosting open rates and
          engagement.
        </Typography>
        <Box>
          <Button onClick={navigateToDNSConfig} variant="text" sx={{ p: 0 }}>
            Update My DNS Settings
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
          },
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            py: 5,
            px: 6,
            gap: 1,
            maxWidth: "40%",
            [theme.breakpoints.down("md")]: {
              maxWidth: "100%",
            },
          })}
        >
          <Typography variant="h3">Sender domain</Typography>
          <Typography
            variant="p2"
            sx={(theme) => ({
              color: theme.palette.grey[80],
            })}
          >
            DNS settings are configured on a domain level rather than individual
            email addresses
          </Typography>
          <Select
            sx={{
              mt: 4,
            }}
            onChange={(e) => handleChangeSelectedDomain(e.target.value)}
            value={selectedDomain || ""}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select a domain
            </MenuItem>
            {domains?.map((option, index) => (
              <MenuItem value={option.domain} key={`option-${index}`}>
                {option.domain}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {selectedDomain && (
          <Box
            sx={() => ({
              display: "flex",
              flexDirection: "column",
              width: "100%",
              py: 5,
              px: 6,
              gap: 1,
            })}
          >
            <Typography variant="h3">Authentication Status</Typography>
            <Divider sx={{ my: 5 }} />
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 5,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {/* DKIM Status */}
                <Box
                  sx={() => ({
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <AuthStatusIndicator
                      isFree={isSelectedDomainFree}
                      isSigned={authStatus.dkim}
                      protocol="DKIM"
                      domain={selectedDomain}
                    />
                  </Box>
                </Box>

                {/* DMARC Status */}
                <Box
                  sx={() => ({
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <AuthStatusIndicator
                      isFree={isSelectedDomainFree}
                      isSigned={authStatus.dmarc}
                      protocol="DMARC"
                      domain={selectedDomain}
                    />
                  </Box>
                </Box>

                {/* SPF Status */}
                <Box
                  sx={() => ({
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <AuthStatusIndicator
                      isFree={isSelectedDomainFree}
                      isSigned={authStatus.spf}
                      protocol="SPF"
                      domain={selectedDomain}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                    onClick={async () => {
                      await checkAuthenticationStatus(selectedDomain);
                      setRecheckFlag(true);
                    }}
                  >
                    <Typography variant="p2">
                      {recheckFlag ? "Recheck" : "Check"}
                    </Typography>
                    {recheckFlag ? <Refresh /> : <ShortRight />}
                  </Button>
                </Box>

                {authError && authError?.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="label1"
                      fontWeight="semiBold"
                      display="block"
                      sx={(theme) => ({
                        color: theme.palette.red[100],
                      })}
                    >
                      {authError}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        window.open(
                          "https://support.tinyalbert.ai/portal/en/kb/articles/authenticate-your-sending-domain-using-dkim",
                          "_blank",
                          "noreferrer"
                        );
                      }}
                    >
                      <ExternalLink />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { DNSAuthentication };
